import axios from "axios"

/**
 * API client. Session cookies flow automatically via withCredentials.
 * 401 interceptor (M14 fix) clears AuthProvider state — page reload kicks user to /login.
 */
export const api = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
  timeout: 15_000,
})

let on401: (() => void) | null = null
export function setOn401(handler: () => void) {
  on401 = handler
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && on401) on401()
    return Promise.reject(err)
  }
)

// ---- Types ----

export type Targets = {
  bmr: number
  tdee: number
  calories: number
  protein_g: number
  fiber_g: number
  carbs_g: number
  sugar_g: number
}

export type Profile = {
  weight_kg: number
  height_cm: number
  age: number
  sex: "male" | "female"
  activity_multiplier: number
  deficit: number
  target_weight_kg?: number
}

export type Recommendation = {
  current_bmi: number
  recommended_target_weight_kg: number
  healthy_weight_min_kg: number
  healthy_weight_max_kg: number
  weekly_loss_kg: number
  weeks_to_goal: number
  max_safe_deficit_kcal: number
  deficit_exceeds_safe: boolean
  min_daily_calories_floor: number
}

export type Me = {
  user: { id: string; email: string; display_name: string; is_admin: boolean }
  profile?: Profile
  targets?: Targets
  recommendation?: Recommendation
}

// ---- Endpoints ----

export async function getMe(): Promise<Me> {
  const { data } = await api.get<Me>("/me")
  return data
}

export async function patchProfile(p: Partial<Profile>): Promise<Me> {
  const { data } = await api.patch<Me>("/me/profile", p)
  return data
}

export async function requestMagicLink(email: string, inviteCode?: string): Promise<void> {
  await api.post("/auth/magic-link/request", {
    email,
    invite_code: inviteCode || undefined,
  })
}

export async function logout(): Promise<void> {
  await api.post("/auth/logout")
}

// ---- Ingredients ----

export type Ingredient = {
  id: string
  name: string
  state: string
  category: string
  source: "usda" | "claude" | "user" | "recipe"
  shared: boolean
  fiber_per_100g: number
  sugar_per_100g: number
  protein_per_100g: number
  carbs_per_100g: number
  calories_per_100g: number
}

export async function searchIngredients(q: string): Promise<Ingredient[]> {
  const { data } = await api.get<{ ingredients: Ingredient[] }>("/ingredients", {
    params: { q },
  })
  return data.ingredients
}

// Unified search across ingredients + saved recipes. Used by QuickMealLog
// so a user can log either via the same picker.
export type Loggable = {
  kind: "ingredient" | "recipe"
  id: string
  name: string
  state?: string
  source?: string
  shared?: boolean
  calories_per_100g: number
  protein_per_100g: number
  fiber_per_100g: number
  carbs_per_100g: number
  sugar_per_100g: number
}

export async function searchItems(q: string): Promise<Loggable[]> {
  const { data } = await api.get<{ items: Loggable[] }>("/items", { params: { q } })
  return data.items
}

export async function createIngredient(p: {
  name: string
  state?: string
  category?: string
  notes?: string
  lookup_via_usda?: boolean
  fiber_per_100g?: number
  sugar_per_100g?: number
  protein_per_100g?: number
  carbs_per_100g?: number
  calories_per_100g?: number
}): Promise<Ingredient> {
  const { data } = await api.post<Ingredient>("/ingredients", p)
  return data
}

export async function deleteIngredient(id: string): Promise<void> {
  await api.delete(`/ingredients/${id}`)
}

// ---- Meals ----

export type MealRow = {
  id: string
  date: string
  meal_type: "breakfast" | "lunch" | "dinner" | "snack"
  quantity_g: number
  notes?: string
  item_id: string
  item_type: "ingredient" | "recipe"
  item_name: string
  calories: number
  protein_g: number
  fiber_g: number
  carbs_g: number
  sugar_g: number
}

export type MealsByDate = {
  date: string
  meals: MealRow[]
  totals: {
    calories: number
    protein_g: number
    fiber_g: number
    carbs_g: number
    sugar_g: number
  }
}

export async function getMealsByDate(date: string): Promise<MealsByDate> {
  const { data } = await api.get<MealsByDate>("/meals", { params: { date } })
  return data
}

export async function createMeal(p: {
  date?: string
  meal_type: string
  ingredient_id?: string
  recipe_id?: string
  quantity_g: number
  notes?: string
}): Promise<{ id: string }> {
  const { data } = await api.post<{ id: string }>("/meals", p)
  return data
}

export async function deleteMeal(id: string): Promise<void> {
  await api.delete(`/meals/${id}`)
}

export async function patchMeal(
  id: string,
  p: {
    meal_type?: "breakfast" | "lunch" | "dinner" | "snack"
    quantity_g?: number
    date?: string
    notes?: string
  }
): Promise<{ id: string; date: string; meal_type: string; quantity_g: number }> {
  const { data } = await api.patch(`/meals/${id}`, p)
  return data
}

export async function getRecentMeals(n = 5): Promise<{ meals: any[] }> {
  const { data } = await api.get("/meals/recent", { params: { n } })
  return data
}

// ---- Meal history (Performance page) ----

export type DayTotals = {
  date: string
  calories: number
  protein_g: number
  fiber_g: number
  carbs_g: number
  sugar_g: number
}

export type MealHistory = {
  start: string
  end: string
  days: DayTotals[]
}

export async function getMealHistory(days = 30): Promise<MealHistory> {
  const { data } = await api.get<MealHistory>("/meals/history", { params: { days } })
  return data
}

// ---- Weight ----

export type WeightEntry = {
  date: string
  weight_kg: number
  notes?: string
}

export async function listWeight(limit = 365): Promise<WeightEntry[]> {
  const { data } = await api.get<{ entries: WeightEntry[] }>("/weight", {
    params: { limit },
  })
  return data.entries
}

export async function upsertWeight(p: {
  date?: string
  weight_kg: number
  notes?: string
}): Promise<WeightEntry> {
  const { data } = await api.post<WeightEntry>("/weight", p)
  return data
}

// ---- Recipes ----

export type ResolvedIngredient = {
  name: string
  state: string
  quantity_g: number
  source: "usda" | "claude" | "user"
  usda_fdc_id?: string
  fiber_per_100g: number
  sugar_per_100g: number
  protein_per_100g: number
  carbs_per_100g: number
  calories_per_100g: number
}

export type ResolvedRecipe = {
  name: string
  source_type: "text" | "url" | "photo" | "name"
  source_content?: string
  servings: number
  total_yield_g: number
  method_summary?: string
  confidence: "high" | "medium" | "low"
  ingredients: ResolvedIngredient[]
  per_100g: {
    calories: number
    protein_g: number
    fiber_g: number
    carbs_g: number
    sugar_g: number
  }
  totals: {
    calories: number
    protein_g: number
    fiber_g: number
    carbs_g: number
    sugar_g: number
  }
}

/** One image in a photo-source parse request. base64 is the raw encoded
 * bytes (no `data:image/...;base64,` prefix). */
export type ParseImagePayload = { base64: string; mime: "image/jpeg" | "image/png" }

export async function parseRecipe(
  sourceType: "text" | "name" | "url" | "photo",
  content: string,
  images?: ParseImagePayload[],
): Promise<{ job_id: string; status: string }> {
  const { data } = await api.post("/recipes/parse", {
    source_type: sourceType,
    content,
    ...(images && images.length > 0 ? { images } : {}),
  })
  return data
}

export type JobStatus = {
  id: string
  type: string
  status: "pending" | "running" | "succeeded" | "failed"
  attempts: number
  result?: ResolvedRecipe
  error?: string
}

export async function getJob(id: string): Promise<JobStatus> {
  const { data } = await api.get<JobStatus>(`/jobs/${id}`)
  return data
}

export async function saveRecipe(
  draft: ResolvedRecipe,
  origin: "saved" | "log" = "saved",
): Promise<{ id: string; name: string }> {
  const { data } = await api.post("/recipes", { draft, origin })
  return data
}

export type Label = {
  id: string
  name: string
  color?: string
}

export type RecipeSummary = {
  id: string
  name: string
  total_yield_g: number
  calories_per_100g: number
  protein_per_100g: number
  created_at: string
  origin: "saved" | "log"
  labels: Label[]
}

/**
 * List recipes filtered by origin and (optionally) label and (optionally)
 * text query.
 *   origin  — "saved" (default) | "log" | "all"
 *   labelId — when set, only recipes that have this label attached
 *   q       — when set, ILIKE + pg_trgm similarity match on name
 */
export async function listRecipes(
  origin: "saved" | "log" | "all" = "saved",
  labelId?: string,
  q?: string,
): Promise<RecipeSummary[]> {
  const params: Record<string, string> = { origin }
  if (labelId) params.label_id = labelId
  if (q && q.trim()) params.q = q.trim()
  const { data } = await api.get<{ recipes: RecipeSummary[] }>("/recipes", { params })
  return data.recipes
}

// ---- Recipe detail ----

export type RecipeIngredient = {
  id: string
  name: string
  state: string
  quantity_g: number
  source: "usda" | "claude" | "user" | "recipe"
  calories_per_100g: number
  protein_per_100g: number
  fiber_per_100g: number
  carbs_per_100g: number
  sugar_per_100g: number
}

export type RecipeDetail = {
  id: string
  name: string
  total_yield_g: number
  source_type: string
  confidence: string
  method_summary: string
  origin: "saved" | "log"
  created_at: string
  per_100g: {
    calories: number
    protein_g: number
    fiber_g: number
    carbs_g: number
    sugar_g: number
  }
  ingredients: RecipeIngredient[]
  labels: Label[]
}

export async function getRecipe(id: string): Promise<RecipeDetail> {
  const { data } = await api.get<RecipeDetail>(`/recipes/${id}`)
  return data
}

export async function deleteRecipe(id: string): Promise<void> {
  await api.delete(`/recipes/${id}`)
}

// ---- Labels ----

export async function listLabels(): Promise<Label[]> {
  const { data } = await api.get<{ labels: Label[] }>("/labels")
  return data.labels
}

export async function createLabel(name: string, color?: string): Promise<Label> {
  const { data } = await api.post<Label>("/labels", { name, color })
  return data
}

export async function deleteLabel(id: string): Promise<void> {
  await api.delete(`/labels/${id}`)
}

/** Replace the recipe's full set of labels with the given ids. */
export async function setRecipeLabels(
  recipeId: string,
  labelIds: string[],
): Promise<Label[]> {
  const { data } = await api.put<{ labels: Label[] }>(
    `/recipes/${recipeId}/labels`,
    { label_ids: labelIds },
  )
  return data.labels
}

// ---- Invites ----

/**
 * Invite — the guestbook record. `status` is server-computed from
 * (used_at, expires_at, now) so the UI doesn't re-derive.
 */
export type InviteStatus = "pending" | "redeemed" | "expired"

export type Invite = {
  code: string
  email_hint: string | null
  expires_at: string
  created_at: string
  used_at: string | null
  created_by_id: string
  created_by_email: string
  created_by_name?: string | null
  used_by_id: string | null
  used_by_email: string | null
  used_by_name?: string | null
  status: InviteStatus
  // Only present on the response from POST /invites (the mint call). Tells
  // the UI whether the server already emailed the recipient or if the
  // inviter still needs to copy-paste the message themselves.
  email_sent?: boolean
  email_error?: string
}

export async function listInvites(): Promise<Invite[]> {
  const { data } = await api.get<{ invites: Invite[] }>("/invites")
  return data.invites
}

export async function createInvite(p: {
  email_hint?: string
  ttl_days?: number
  send_email?: boolean
}): Promise<Invite> {
  const { data } = await api.post<Invite>("/invites", {
    email_hint: p.email_hint?.trim() || undefined,
    ttl_days: p.ttl_days,
    send_email: p.send_email,
  })
  return data
}

export async function deleteInvite(code: string): Promise<void> {
  await api.delete(`/invites/${encodeURIComponent(code)}`)
}

// ---- Activity multiplier presets ----

export const ACTIVITY_PRESETS = [
  { label: "Sedentary", desc: "desk job, no exercise", value: 1.2 },
  { label: "Light", desc: "1–3 workouts / week", value: 1.375 },
  { label: "Moderate", desc: "3–5 workouts / week", value: 1.55 },
  { label: "Active", desc: "6–7 workouts / week", value: 1.725 },
  { label: "Very active", desc: "physical job + training", value: 1.9 },
] as const
